import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Typography } from "@material-ui/core";
import { ExportViewProps, ExportViewRef } from "./models";
import SearchFilter, { Filters } from "../../../Common/SearchFilter/SearchFilter";
import { searchFilterToExportExcelFilter } from "../../../Common/SearchFilter/utils";
import useDidMountEffect from "../../../Common/UseDidMountEffect";
import { ApiBackend } from "../../../../providers/apibackend";
import { Event } from "../../../../model/Event";
import { Product } from "../../../../model/Product";

type IncludeFilterType = {
    raceWeek?: string[];
    discipline?: string;
    grouping?: string;
    minAge?: number;
    okForCommercialFilter?: boolean;
    okForThirdPartyFilter?: boolean;
    onlyNational?: boolean;
    onlyRaces?: string[];
};

type ExcludeFilterType = {
    notEnteredEventId?: string;
};

interface IIncludeFilter {
    raceWeek?: string[];
    discipline?: string;
    grouping?: string;
    minAge?: number;
    okForCommercialFilter?: boolean;
    okForThirdPartyFilter?: boolean;
    onlyRaces?: string[];
}

interface IExcludeFilter {
    notEnteredEventId?: string;
}

const EnteredForRaceWeek = forwardRef<ExportViewRef, ExportViewProps>((props, ref) => {
    const [includeFilter, setIncludeFilter] = useState(null as IIncludeFilter);
    const [excludeFilter, setExcludeFilter] = useState(null as IExcludeFilter);
    const [events, setEvents] = useState(null as Event[]);
    const [races, setRaces] = useState(null as Product[]);
    useImperativeHandle(ref, () => ({
        isValid: () => {
            // Minst ett evenemang måste väljas. Disciplin är valfritt.
            return !!includeFilter?.raceWeek && includeFilter.raceWeek.length > 0;
        }
    }));

    useEffect(() => {
        let backend = new ApiBackend();
        backend.listEvents().then((res) => {
            setEvents(res);
        });
        backend.listProducts().then((res) => {
            setRaces(res);
        });
    }, []);

    const disciplines = [{ name: "Skidor", value: "ski" }, { name: "Cykel", value: 'bike' }, { name: 'Löpning', value: 'run' }];

    useDidMountEffect(() => {
        // Slå ihop filtren från båda sektionerna till en export-begäran.
        const inc = searchFilterToExportExcelFilter<IIncludeFilter>(includeFilter) || [];
        const exc = searchFilterToExportExcelFilter<IExcludeFilter>(excludeFilter) || [];
        props.onChange([...inc, ...exc]);
    }, [includeFilter, excludeFilter]);

    const render = () => {

        // "Enbart lopp" går bara att filtrera på när exakt ETT evenemang är valt.
        const selectedEventIds = includeFilter?.raceWeek || [];
        const singleEventId = selectedEventIds.length === 1 ? selectedEventIds[0] : null;
        const selRaces = singleEventId ? races?.filter(x => x.EventId == singleEventId && x.type == "race") : [];
        const eventItems = events == null ? [] : events.map(x => { return { name: x.name, value: x.id } });

        return (
            <>
                <Typography variant="h6" style={{ marginTop: 8 }}>Inkludera</Typography>
                <SearchFilter<IncludeFilterType>
                    id={"export-enteredforraceweek-include"}
                    filters={{
                        "raceWeek": {
                            id: "filter-raceWeek",
                            type: "GenericSelect",
                            label: "Evenemang (ett eller flera)",
                            size: 12,
                            multiple: true,
                            itemDefinition: {
                                items: eventItems,
                                itemValueType: "string"
                            }
                        },
                        "discipline": {
                            id: "filter-discipline",
                            type: "GenericSelect",
                            label: "Diciplin (valfritt)",
                            size: 12,
                            multiple: false,
                            clearable: true,
                            itemDefinition: {
                                items: disciplines == null ? [] : disciplines.map(x => { return { name: x.name, value: x.value } }),
                                itemValueType: "string"
                            }
                        },
                        "grouping": {
                            id: "filter-grouping",
                            type: "GenericSelect",
                            label: "Gruppering",
                            size: 12,
                            multiple: false,
                            defaultValue: "personPerRace",
                            itemDefinition: {
                                items: [
                                    { name: "Person per lopp (en rad per person och lopp)", value: "personPerRace" },
                                    { name: "Person globalt (en rad per person)", value: "personGlobal" },
                                    { name: "Ingen (en rad per anmälan)", value: "none" },
                                    { name: "E-post per lopp (en rad per e-post och lopp)", value: "emailPerRace" },
                                    { name: "E-post globalt (en rad per e-post)", value: "emailGlobal" }
                                ],
                                itemValueType: "string"
                            }
                        },
                        "okForCommercialFilter": {
                            id: "filter-okForCommercialFilter",
                            type: "Checkbox",
                            label: "Applicera filter ok med reklam",
                            size: 12,
                            defaultValue: false,
                            multiple: false
                        },
                        "okForThirdPartyFilter": {
                            id: "filter-okForThirdPartyFilter",
                            type: "Checkbox",
                            label: "Applicera filter ok med 3:e part",
                            size: 12,
                            defaultValue: false,
                            multiple: false
                        },
                        "minAge": {
                            id: "filter-minAge",
                            type: "GenericSelect",
                            label: "Filter minsta ålder (valfritt)",
                            size: 12,
                            multiple: false,
                            itemDefinition: {
                                items: [16, 18].map(x => { return { name: x + '', value: x } }),
                                itemValueType: "number"
                            }
                        },
                        "onlyNational": {
                            id: "filter-onlyNational",
                            type: "Checkbox",
                            label: "Enbart nationella (valfritt)",
                            size: 12,
                            defaultValue: false,
                            multiple: false
                        },
                        "onlyRaces": {
                            id: "filter-onalyraces",
                            type: "GenericSelect",
                            label: singleEventId ? "Enbart lopp (valfritt)" : "Enbart lopp (endast när ett evenemang är valt)",
                            size: 12,
                            multiple: true,
                            itemDefinition: {
                                items: selRaces == null ? [] : selRaces.map(x => { return { name: x.name + '', value: x.id } }),
                                itemValueType: "string"
                            }
                        },
                    }}
                    persist={true}
                    onInit={(filter: Filters<IncludeFilterType>) => {
                        setIncludeFilter(filter as unknown as IIncludeFilter);
                    }}
                    onChange={(filter: Filters<IncludeFilterType>) => {
                        setIncludeFilter(filter as unknown as IIncludeFilter);
                    }}
                />

                <Typography variant="h6" style={{ marginTop: 16 }}>Exkludera</Typography>
                <SearchFilter<ExcludeFilterType>
                    id={"export-enteredforraceweek-exclude"}
                    filters={{
                        "notEnteredEventId": {
                            id: "filter-notenteredevent",
                            type: "GenericSelect",
                            label: "Person ej anmäld till evenemang (valfritt)",
                            size: 12,
                            multiple: false,
                            clearable: true,
                            itemDefinition: {
                                items: eventItems,
                                itemValueType: "string"
                            }
                        },
                    }}
                    persist={true}
                    onInit={(filter: Filters<ExcludeFilterType>) => {
                        setExcludeFilter(filter as unknown as IExcludeFilter);
                    }}
                    onChange={(filter: Filters<ExcludeFilterType>) => {
                        setExcludeFilter(filter as unknown as IExcludeFilter);
                    }}
                />
            </>
        );
    };

    return render();
});

export default EnteredForRaceWeek;
